import intersection from "lodash/intersection";

import { getWindow, notEmpty } from "shared/lib/utils";

import { OneDayInMilliseconds } from "./popups/campaign/consts";
import type { GeoTargeting, PageTargeting, ScmpPwaWidgetPPTOConfig } from "./types";

type GetIsGeoTargetingParameters = {
  geoTargeting: GeoTargeting | undefined;
  userCountry: string;
};

const getIsGeoTargeting = ({ geoTargeting, userCountry }: GetIsGeoTargetingParameters) => {
  if (!geoTargeting) {
    return true;
  }

  const { type, userCountries } = geoTargeting;

  if (userCountries.length === 0) {
    return true;
  }

  switch (type) {
    case "exclude":
      return !userCountries.includes(userCountry);
    case "include":
      return userCountries.includes(userCountry);
    default:
      return true;
  }
};

type GetIsPageTargetingParameters = {
  location: Location | undefined;
  pageTargeting: PageTargeting | undefined;
};

const getIsPageTargeting = ({ location, pageTargeting }: GetIsPageTargetingParameters) => {
  if (!location || !pageTargeting) {
    return true;
  }

  const { excludedPathnameRegexList, excludedURLList, includedPathnameRegexList, includedURLList } =
    pageTargeting;

  const isExcludedPathnameMatched =
    !excludedPathnameRegexList ||
    excludedPathnameRegexList.every(
      excludedPathnameRegex => !new RegExp(excludedPathnameRegex).test(location.pathname),
    );
  const isExcludedURLMatched =
    !excludedURLList || excludedURLList.every(excludedURL => !location.href.includes(excludedURL));
  const isIncludedPathnameMatched =
    !includedPathnameRegexList ||
    includedPathnameRegexList.length === 0 ||
    includedPathnameRegexList.some(includedPathnameRegex =>
      new RegExp(includedPathnameRegex).test(location.pathname),
    );
  const isIncludedURLMatched =
    !includedURLList ||
    includedURLList.length === 0 ||
    includedURLList.some(includedURL => location.href.includes(includedURL));

  return (
    isExcludedPathnameMatched &&
    isExcludedURLMatched &&
    isIncludedPathnameMatched &&
    isIncludedURLMatched
  );
};

type GetIsUserTypeTargetingParameters = {
  userTypeIds: string[] | undefined;
  userTypeTargeting: string[] | undefined;
};

const getIsUserTypeTargeting = ({
  userTypeIds,
  userTypeTargeting,
}: GetIsUserTypeTargetingParameters) => {
  if (!notEmpty(userTypeTargeting)) {
    return true;
  }

  if (!notEmpty(userTypeIds)) {
    return false;
  }

  return notEmpty(intersection(userTypeIds, userTypeTargeting));
};

type GetShouldShowParameters = {
  config: ScmpPwaWidgetPPTOConfig;
  userInfo: PPTOUserInfo;
};

type PPTOUserInfo = {
  isLoggedIn: boolean;
  userCountry: string;
  userDevice: "desktop" | "mobile";
  userTypeIds: string[] | undefined;
};

export const getShouldShow = ({ config, userInfo }: GetShouldShowParameters) => {
  const hasNotShownYet = [
    config.localStorageKey,
    ...(config.disableWithLocalStorageKeys ?? []),
  ].every(localStorageKey => localStorage.getItem(localStorageKey) === null);

  const isGeoTargeting = getIsGeoTargeting({
    geoTargeting: config.geoTargeting,
    userCountry: userInfo.userCountry,
  });

  const isPageTargeting = getIsPageTargeting({
    location: getWindow()?.location,
    pageTargeting: config.pageTargeting,
  });

  const isUserTypeTargeting = getIsUserTypeTargeting({
    userTypeIds: userInfo.userTypeIds,
    userTypeTargeting: config.userTypeTargeting,
  });

  const isDisabled = config.disableInMobile && userInfo.userDevice === "mobile";

  const isTargetUser =
    (config.anonymous && !userInfo.isLoggedIn) || (config.login && userInfo.isLoggedIn);

  return (
    hasNotShownYet &&
    isGeoTargeting &&
    isPageTargeting &&
    isUserTypeTargeting &&
    isTargetUser &&
    !isDisabled
  );
};

type GetLoginDialogTitleAndDescriptionParameters = {
  campaignName: string;
  type: string;
};

export const getLoginDialogTitleAndDescription = (
  parameters: GetLoginDialogTitleAndDescriptionParameters,
) => {
  const { campaignName, type } = parameters;

  if (campaignName === "myNewsSetup") {
    // Follow existing login popup in v1
    return {
      description: "",
      title: "Sign In / Register",
    };
  }
  return {
    description: `Log in or register to follow <span class="bold-text">${campaignName}</span> and get notified on newly published stories.`,
    title: `Get notified on your favourite ${type}`,
  };
};

export const getPopupShownTimestamp = (): null | number => {
  const popupShownTimestamp = sessionStorage.getItem("popupShownTimestamp");
  return popupShownTimestamp ? Number.parseInt(popupShownTimestamp, 10) : null;
};

export const setPopupShownTimestampSession = (): void => {
  const expirationTime = Date.now() + OneDayInMilliseconds;
  sessionStorage.setItem("popupShownTimestamp", expirationTime.toString());
};

export const isPopupExpired = (timestamp: number): boolean =>
  Date.now() - timestamp > OneDayInMilliseconds;
